import { useState } from "react";
import { toast } from "sonner";
import { CheckCircle2 } from "lucide-react";
import { Link } from "react-router-dom";
import { submitRep } from "@/lib/api";
import {
    TextField,
    SelectField,
    CheckboxGroupField,
} from "@/components/forms/FormFields";

const TERRITORIES = [
    "United States — Nationwide",
    "Northeast",
    "Southeast",
    "Midwest",
    "Southwest",
    "West",
    "Other",
];

const SPECIALTIES = [
    "Foot & Ankle",
    "Trauma",
    "Total Joints / Arthroplasty",
    "Spine",
    "Sports Medicine",
    "Hand / Upper Extremity",
    "Podiatry",
    "Pediatric Orthopedics",
    "Other",
];

const initial = {
    full_name: "",
    email: "",
    phone: "",
    territory: "",
    specialties: [],
    // honeypot field — bots fill hidden inputs, humans don't see it
    website: "",
};

function SectionHeading({ title, description }) {
    return (
        <div className="border-b border-slate-200 pb-3">
            <h3 className="font-display text-xl md:text-2xl font-semibold text-slate-900 tracking-tight">
                {title}
            </h3>
            {description && (
                <p className="mt-1.5 text-sm text-slate-500">{description}</p>
            )}
        </div>
    );
}

export default function RepForm() {
    const [form, setForm] = useState(initial);
    const [loading, setLoading] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    const set = (key) => (value) => setForm((f) => ({ ...f, [key]: value }));

    const validate = () => {
        const required = ["full_name", "email", "territory"];
        for (const k of required) {
            if (!form[k] || (typeof form[k] === "string" && !form[k].trim())) {
                toast.error("Please complete all required fields.");
                return false;
            }
        }
        return true;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (loading) return;
        if (form.website) {
            // silently accept honeypot hits
            setSubmitted(true);
            return;
        }
        if (!validate()) return;
        setLoading(true);
        try {
            const payload = {
                full_name: form.full_name.trim(),
                email: form.email.trim(),
                phone: form.phone.trim(),
                territory: form.territory,
                specialties: form.specialties,
                selected_opportunity: "InduceXT — Nationwide",
                products_to_rep: ["InduceXT"],
                honeypot: form.website,
            };
            await submitRep(payload);
            setSubmitted(true);
            window.scrollTo({ top: 0, behavior: "smooth" });
        } catch (err) {
            console.error(err);
            const msg =
                err?.response?.data?.detail ||
                "Submission failed. Please check your entries and try again.";
            toast.error(typeof msg === "string" ? msg : "Submission failed. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    if (submitted) {
        return (
            <section className="bg-[#FAFAFA] border-b border-slate-200">
                <div className="max-w-3xl mx-auto px-6 md:px-12 py-16 md:py-20">
                    <div
                        data-testid="rep-form-confirmation"
                        className="bg-white border border-slate-200 rounded-2xl p-10 md:p-14 text-center"
                    >
                        <div className="w-12 h-12 mx-auto rounded-full bg-emerald-50 border border-emerald-100 flex items-center justify-center">
                            <CheckCircle2 className="text-emerald-600" size={24} />
                        </div>
                        <h3 className="font-display mt-6 text-2xl md:text-3xl font-semibold text-slate-900">
                            Thank you. Your interest has been submitted.
                        </h3>
                        <p className="mt-4 text-base text-slate-600 max-w-xl mx-auto leading-relaxed">
                            1099er will review your submission and reach out privately
                            if there is a fit. Nothing is committed until we speak.
                        </p>
                    </div>
                </div>
            </section>
        );
    }

    return (
        <section className="bg-[#FAFAFA] border-b border-slate-200">
            <div className="max-w-3xl mx-auto px-6 md:px-12 py-16 md:py-20">
                <form
                    data-testid="rep-form"
                    onSubmit={handleSubmit}
                    noValidate
                    className="bg-white border border-slate-200 rounded-2xl p-7 md:p-12 space-y-10"
                >
                    {/* Honeypot — hidden from real users */}
                    <div aria-hidden="true" className="hidden">
                        <label>
                            Website
                            <input
                                type="text"
                                tabIndex={-1}
                                autoComplete="off"
                                value={form.website}
                                onChange={(e) => set("website")(e.target.value)}
                            />
                        </label>
                    </div>

                    <section className="space-y-6" data-testid="rep-section-contact">
                        <SectionHeading
                            title="Contact Info"
                            description="We only use this to reach out privately about a qualified fit."
                        />
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <TextField
                                label="Name"
                                required
                                value={form.full_name}
                                onChange={set("full_name")}
                                testId="rep-full-name"
                            />
                            <TextField
                                label="Email"
                                required
                                type="email"
                                value={form.email}
                                onChange={set("email")}
                                testId="rep-email"
                            />
                            <TextField
                                label="Phone (optional)"
                                value={form.phone}
                                onChange={set("phone")}
                                testId="rep-phone"
                            />
                            <SelectField
                                label="Territory"
                                required
                                value={form.territory}
                                onChange={set("territory")}
                                options={TERRITORIES}
                                testId="rep-territory"
                            />
                        </div>
                        <CheckboxGroupField
                            label="Specialties (optional)"
                            values={form.specialties}
                            onChange={set("specialties")}
                            options={SPECIALTIES}
                            testId="rep-specialties"
                        />
                    </section>

                    <div className="pt-2 flex flex-col sm:flex-row justify-end gap-3">
                        <button
                            type="submit"
                            disabled={loading}
                            data-testid="rep-submit-btn"
                            className="bg-slate-900 text-white text-sm font-medium px-7 py-3.5 rounded-md hover:bg-slate-800 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                        >
                            {loading ? "Submitting..." : "Request Confidential Call"}
                        </button>
                        <Link
                            to="/faq"
                            data-testid="rep-faq-btn"
                            className="inline-flex items-center justify-center bg-white text-slate-900 border border-slate-300 text-sm font-medium px-7 py-3.5 rounded-md hover:border-slate-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-900 focus-visible:ring-offset-2 transition-colors"
                        >
                            FAQ
                        </Link>
                    </div>
                </form>
            </div>
        </section>
    );
}
